import environment from '../../../common/environment';
import { CreateCoverImage } from '../interfaces/create-custom-product.interface';
import { DEFAULT_COVER_IMAGE, getImageSrcUrl } from '../../../common/utils/product-util';
import { OfferType, OfferTypeChecker } from '../../../common/utils/offer-type-util';
import { OrderType } from '../../../common/constants/order-types';
import { ProductCardData } from '../../product-card/product-card-display';
import { html, nothing, TemplateResult } from 'lit';
import { formattedJoin } from '../../../common/utils/util';
import { hasAuthorInfo } from '../../product-card/utils/product-card.util';
import { PaymentOrderItemData } from '../../order-confirmation/order-completion.interface';
import { CartFormatCode } from '../../../common/utils/cart-util';

export const CREATE_CUSTOM_CONTRIBUTOR_LABEL = 'By';

/**
 * Displays the product image for Create/custom.
 *
 * The image is retrieved differently for Create ebooks than for other products.
 */
export function getCreateCustomImage({
  altText = '',
  createCoverImages,
  identifier = '',
  image,
  isCreateEbook,
}: {
  altText?: string;
  createCoverImages?: CreateCoverImage[];
  identifier: string;
  image?: string;
  isCreateEbook: boolean;
}): TemplateResult {
  if (isCreateEbook) {
    return html`
      <img
        src="${getCreateCoverImage(createCoverImages)}"
        onerror="this.onerror=null; this.src='${DEFAULT_COVER_IMAGE}';"
        class="img-fluid object-fit-contain"
        alt="${altText}"/>
    `;
  }

  return html`
    <img
      src="${image}"
      onerror="this.onerror=null; this.src='${getImageSrcUrl(identifier)}';"
      class="img-fluid object-fit-contain"
      alt="${altText}"/>
  `;
}

/**
 * Returns the Create cover image URL for the product.
 * It is displayed on the cart.
 *
 * @param createCoverImages
 */
export function getCreateCoverImage(createCoverImages?: CreateCoverImage[]): string {
  const coverPath = createCoverImages ? createCoverImages[0]?.httpUrl : undefined;

  return `${environment().createUrl}/createonline/covers/personalize/${coverPath}.jpg`;
}

export function getCreateProductUrl(isbn13: string): string {
  return `${environment().mheducationUrl}/highered/custom/product/${isbn13}.html`;
}

export function getCustomProductUrl(store: string, isbn13: string): string {
  return `${environment().mheducationUrl}/highered/custom/store/${store}/product/${isbn13}.html`;
}

/**
 * Retrieves the order type based on the product offer type for Create and Custom products.
 *
 * @param product
 */
export function getCreateCustomOrderType(product: ProductCardData): OrderType | string {
  switch (product.offerType?.toLowerCase()) {
    case OfferType.EBOOK_CREATE:
      return OrderType.HE_CREATE;
    case OfferType.CUSTOM:
      return OrderType.HE_CUSTOM_STORE;
    default:
      return '';
  }
}

/**
 * Displays the object property if it exists.
 *
 * @param obj
 * @param code
 * @param label
 * @param separator
 */
export function renderCreateCustomProperty(
  obj: any,
  code: string,
  label: string,
  separator = ''
): TemplateResult | typeof nothing {
  if (!obj) {
    return nothing;
  }

  const value = obj[code];
  if (value === null || value === undefined || value === '') {
    return nothing;
  }

  return html`<div><strong> ${label}${separator} </strong>${value}</div>`;
}

/**
 * Sanitizes the instructors by removing any 'null' strings and trimming whitespaces.
 *
 * @param instructors
 */
export function sanitizeInstructors(instructors: string[]): string[] {
  if (instructors.length === 0) {
    return [];
  }

  return instructors.map((displayName) => displayName?.replace('null', '').trim() ?? '').filter(Boolean);
}

export function renderCreateCustomProductCardMetadata({
  isCreateEbook,
  copyrightYear,
  product,
  showProductType,
  parsedAuthor,
}: {
  isCreateEbook: boolean;
  copyrightYear: string;
  product: ProductCardData | PaymentOrderItemData;
  showProductType?: boolean;
  parsedAuthor?: boolean;
}): TemplateResult {
  return html`
    ${isCreateEbook
      ? renderCreateEbookMetadata(product, showProductType)
      : renderCustomMetadata(product, parsedAuthor)
    }
    ${copyrightYear ? html`<div><span>&#169;</span>${copyrightYear}</div>` : nothing}
  `
}

/**
 * Displays metadata specific to Create ebooks (custombook offer type).
 *
 * @param product
 */
function renderCreateEbookMetadata(product, showProductType?: boolean): TemplateResult {
  return html`
    ${showProductType ? html`<div><strong>Create Product</strong></div>`: nothing}
    ${renderCreateCustomProperty(product, 'schoolName', 'School')}
    ${renderCreateCustomProperty(product, 'department', 'Department')}
    ${product.instructorNames && product.instructorNames.length > 0
      ? html`<div><strong> Instructor </strong>${product.instructorNames[0].replace('null', '').trim()}</div>`
      : nothing}
    ${renderCreateCustomProperty(product, 'courseNumber', 'Course number')}
    ${renderCreateCustomProperty(product, 'isbn13', 'ISBN')}
    <div><strong> Type </strong>eBook</div>
    ${renderCreateCustomProperty(product, 'format', 'Format')}
  `;
}

/**
 * Displays metadata for custom products that are not Create ebooks (privatebook offer type).
 *
 * @param product
 */
function renderCustomMetadata(product, parsedAuthor?: boolean): TemplateResult {
  return html`
    ${parsedAuthor
      ? renderCreateCustomProperty(product, 'authors', CREATE_CUSTOM_CONTRIBUTOR_LABEL)
      : hasAuthorInfo(product)
        ? html`<div><strong>${CREATE_CUSTOM_CONTRIBUTOR_LABEL}</strong> ${formattedJoin(product.contributors!)}</div>`
        : nothing
    }
    ${renderCreateCustomProperty(product, 'courseNumber', 'Course number')}
    ${renderCreateCustomProperty(product, 'isbn13', 'ISBN')}
    ${renderCreateCustomProperty(product, 'format', 'Type')}
  `;
}

export function getHeStoreContinueShoppingRedirectUrl(products: PaymentOrderItemData[]): string {
  const allProductsAreCreateEbooks =
    products?.length > 0 && products.every((product) => OfferTypeChecker.isCreateEbook(product.offerType));

  return allProductsAreCreateEbooks ? `${environment().mheducationUrl}/highered/bookstore.html` : `${environment().mheducationUrl}`;
}

export function getHeStoreDataForAnalytics({
  isCreateEbook,
  formatDescription,
  formatTypeDescription,
}: {
  isCreateEbook: boolean;
  formatDescription?: string;
  formatTypeDescription?: string;
}): Record<string, any> {
  const details: Record<string, any> = {};

  if (isCreateEbook) {
    details.type = CartFormatCode.eBook;
    details.format = formatDescription;
  } else {
    details.type = formatDescription;
    details.format = formatTypeDescription;
  }

  return details;
}

export const CUSTOM_STORE_LIST_CARD_VARIATION = 'custom-store-list-card';
